import {
  useGetInterviewStateApiV1InterviewsStateGet,
  getInterviewStateApiV1InterviewsStateGet
} from '../api/endpoints/openapi-generated'
import { withSentryOnError } from '../utils/sentry/with-sentry-on-error'
import {
  VacancyResource,
  InterviewResource,
  MessageResource,
  type GetInterviewStateResponse,
  type HTTPValidationError
} from '../api/endpoints/openapi-generated.schemas'
import {
  QueryKey,
  UseQueryResult,
  QueryCacheNotifyEvent
} from '@tanstack/react-query'
import { useEffect } from 'react'
import { queryClient } from '../query-client'
import { preloadImage } from 'utils/preload'
import { type ApiError } from '../api/mutator/platform-instance'

type InterviewStateQueryData = GetInterviewStateResponse

export async function prefetchInterviewState(
  vacancy: VacancyResource,
  interview: InterviewResource
): Promise<void> {
  const queryKey = getInterviewStateQueryKey({
    vacancyId: vacancy.id,
    interviewId: interview.id
  })

  await queryClient.prefetchQuery({
    queryKey,
    retry: false,
    queryFn: () => {
      return getInterviewStateApiV1InterviewsStateGet({
        vacancy_id: vacancy.id
      })
    }
  })
}

export function getInterviewStateDataFromEvent(
  event: QueryCacheNotifyEvent,
  vacancy: VacancyResource,
  interview: InterviewResource
): InterviewStateQueryData | null | undefined {
  if (event.type !== 'updated') return

  const queryKey = getInterviewStateQueryKey({
    vacancyId: vacancy.id,
    interviewId: interview.id
  })
  if (event.query.queryKey.toString() !== queryKey.toString()) return

  const data = event.query.state.data as InterviewStateQueryData | undefined
  return data ?? null
}

function getInterviewStateQueryKey({
  vacancyId,
  interviewId
}: {
  vacancyId: string
  interviewId: string
}): QueryKey {
  return ['getInterviewState', vacancyId, interviewId]
}

export function getInitialMessages(
  vacancy: VacancyResource,
  interview: InterviewResource
): Array<MessageResource> | undefined {
  return queryClient.getQueryData<InterviewStateQueryData>(
    getInterviewStateQueryKey({
      vacancyId: vacancy.id,
      interviewId: interview.id
    })
  )?.data.messages
}

export function useInterviewState(
  vacancy: VacancyResource,
  interview: InterviewResource
): UseQueryResult<InterviewStateQueryData, ApiError<HTTPValidationError>> {
  const queryKey = getInterviewStateQueryKey({
    vacancyId: vacancy.id,
    interviewId: interview.id
  })

  const result = useGetInterviewStateApiV1InterviewsStateGet<
    InterviewStateQueryData,
    ApiError<HTTPValidationError>
  >(
    { vacancy_id: vacancy.id },
    {
      query: {
        staleTime: Infinity,
        retry: false,
        queryKey,
        throwOnError: withSentryOnError({
          type: 'query',
          queryKey
        })
      }
    }
  )

  useEffect(() => {
    result.data?.data.presentation_slides_urls?.forEach((el) =>
      preloadImage(el)
    )
  }, [vacancy, interview, result.data])

  return result
}
